import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner@2.0.3';
import { supabase } from '@/lib/supabase';
import { MapPin, Phone } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);

  // simple honeypot to deter bots (hidden input below)
  const [botField, setBotField] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // client-side validation
    const name = formData.name.trim();
    const email = formData.email.trim().toLowerCase();
    const message = formData.message.trim();

    if (!name || !email || !message) {
      toast.error('Please fill out all fields.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Please provide a valid email address.');
      return;
    }

    // if honeypot filled, silently accept without storing
    if (botField) {
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    setLoading(true);
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : undefined;
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

      const { data: u } = await supabase.auth.getUser();
      const user_id = u?.user?.id ?? null;

      const { error } = await supabase.from('contact_messages').insert([
        {
          name,
          email,
          message,
          user_id,
          path,
          user_agent: userAgent,
        },
      ]);

      if (error) {
        console.error('[contact] insert error', error);
        toast.error('Could not submit your message. Please try again.');
        return;
      }

      toast.success("Thank you! We'll be in touch soon.");
      setFormData({ name: '', email: '', message: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen py-24 px-6 flex flex-col items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full"
      >
        <div className="text-center mb-12">
          <h2 className="text-white mb-4">Get In Touch</h2>
          <p className="text-cyan-300 text-xl">
            Interested in joining or partnering with EdgeAI Academy? Contact us today.
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="p-8 rounded-xl relative"
          style={{
            background: 'rgba(15, 35, 60, 0.7)',
            backdropFilter: 'blur(12px)',
            border: '2px solid rgba(0, 229, 209, 0.3)',
            boxShadow: '0 0 60px rgba(0, 229, 209, 0.2)',
          }}
        >
          {/* Honeypot (hidden from humans) */}
          <input
            tabIndex={-1}
            autoComplete="off"
            value={botField}
            onChange={(e) => setBotField(e.target.value)}
            className="hidden"
            aria-hidden="true"
          />

          <div
            className="absolute inset-0 rounded-xl opacity-50 pointer-events-none"
            style={{
              background:
                'linear-gradient(45deg, transparent 30%, rgba(0, 229, 209, 0.1) 50%, transparent 70%)',
              backgroundSize: '200% 200%',
              animation: 'shimmer 3s ease-in-out infinite',
            }}
          />

          <div className="relative z-10 space-y-6">
            <div>
              <Label htmlFor="name" className="text-cyan-200 mb-2 block">
                Name
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={loading}
                className="bg-transparent border-cyan-400/30 text-white focus:border-cyan-400 transition-colors"
                style={{ boxShadow: '0 0 10px rgba(0, 229, 209, 0.1)' }}
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-cyan-200 mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={loading}
                className="bg-transparent border-cyan-400/30 text-white focus:border-cyan-400 transition-colors"
                style={{ boxShadow: '0 0 10px rgba(0, 229, 209, 0.1)' }}
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-cyan-200 mb-2 block">
                Message
              </Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                disabled={loading}
                rows={6}
                className="bg-transparent border-cyan-400/30 text-white focus:border-cyan-400 transition-colors resize-none"
                style={{ boxShadow: '0 0 10px rgba(0, 229, 209, 0.1)' }}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-6"
              style={{ boxShadow: '0 0 30px rgba(0, 229, 209, 0.4)' }}
            >
              {loading ? 'Sending…' : 'Send Message'}
            </Button>
          </div>
        </motion.form>

        <style>{`
          @keyframes shimmer {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
        `}</style>
      </motion.div>

      {/* Additional Contact Info (now below form, dark theme) */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-10 max-w-2xl w-full"
      >
        <Card
          className="bg-[#050b18]/90 backdrop-blur-lg border border-cyan-400/30 
                     shadow-[0_0_40px_rgba(0,229,209,0.25)]"
        >
          <CardContent className="pt-6 pb-6">
            <h3 className="text-cyan-100 text-2xl mb-6">Contact Us</h3>
            <div className="space-y-6 text-sm">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <MapPin className="text-cyan-400" size={28} />
                </div>
                <div className="space-y-1">
                  <p className="text-slate-100 font-medium">MindEngage Inc</p>
                  <p className="text-slate-300">84 W. Santa Clara St., #700</p>
                  <p className="text-slate-300">San Jose, CA 95113</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <Phone className="text-rose-400" size={28} />
                </div>
                <div>
                  <a
                    href="tel:+18669536888"
                    className="text-slate-100 hover:text-cyan-300 transition-colors font-medium"
                  >
                    1 866 953 6888
                  </a>
                  <p className="text-slate-400 text-xs mt-1">
                    Call us for partnerships, curriculum, and platform inquiries.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
